import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react";

type Variant = "success" | "error" | "info";
interface Toast {
  id: number;
  message: string;
  variant: Variant;
}

interface ToastValue {
  push: (message: string, variant?: Variant) => void;
}

const ToastContext = createContext<ToastValue | null>(null);
const ICONS = { success: CheckCircle2, error: AlertTriangle, info: Info };
const TONES: Record<Variant, string> = {
  success: "text-emerald-400",
  error: "text-rose-400",
  info: "text-brand-300",
};

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (message: string, variant: Variant = "info") => {
      const id = nextId++;
      setToasts((current) => [...current.slice(-3), { id, message, variant }]);
      window.setTimeout(() => dismiss(id), variant === "error" ? 7000 : 4000);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 bottom-4 z-100 flex flex-col items-center gap-2 px-4 sm:bottom-6"
        role="region"
        aria-live="polite"
      >
        {toasts.map((toast) => {
          const Icon = ICONS[toast.variant];
          return (
            <div
              key={toast.id}
              className="animate-rise glass pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-2xl px-4 py-3 shadow-lift"
            >
              <Icon className={"mt-0.5 size-5 shrink-0 " + TONES[toast.variant]} aria-hidden />
              <p className="flex-1 text-sm leading-relaxed" dir="auto">
                {toast.message}
              </p>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                className="rounded-lg p-1 text-[var(--color-ink-faint)] transition hover:text-[var(--color-ink)]"
                aria-label="Dismiss"
              >
                <X className="size-4" aria-hidden />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error("useToast must be used inside ToastProvider");
  return value;
}
